import {
  Button,
  Checkbox,
  H3,
  Icon,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
} from '@blueprintjs/core';
import {
  Cell,
  Column,
  ColumnHeaderCell,
  RegionCardinality,
  RowHeaderCell,
  Table2,
  TableLoadingOption,
  TruncatedFormat2,
} from '@blueprintjs/table';
import '@blueprintjs/table/lib/css/table.css';
import PagingFooter from 'Components/Generic/AdvancedDataTable/PagingFooter';

import ExportButton from 'Components/Generic/Buttons/ExportButton';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import ShowOnHoverWrapper from 'Lib/hoc/ShowOnHoverWrapper';
import { useAutoRefresh } from 'Lib/hooks/useAutoRefresh';

import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { areEquivalent, formatMoney } from 'Util';
import './DataTable.css';
import styles from './DataTable.module.css';

const TablePagingContext = createContext({});
TablePagingContext.displayName = 'TablePagingContext';

const DataTableComponent = ({
  data,
  initialData,
  dataQuery,
  dataQueryFunc,
  autoRefreshInterval = 0, // Default auto refresh to OFF
  refreshKey,
  isBusy = false,
  headers = [],
  hiddenColumns,
  setHiddenColumns,
  onUpdatePagingInfo,
  title,
  persistentPaging = {},
  rowHeight = 46,
  onClearFilter,
  filterBy,
  exportDataFunc,
  actionButtons,
  rerenderDependencies = [],
  measureContainer,
  columnWidthsForTable = [],
  updateColumnWidth,
  adjustRowHeightToFit = false,
  smallFont = false,
  setSmallFont,
  includeCheckBoxCol,
  idField = 'id',
  selectedItems,
  onToggleCheckboxForItem,
  onToggleParentCheckbox,
  highlightFunc,
  navigateToDetailsFunc,
}) => {
  const tableBox = useRef();
  const blueprintTable = useRef();

  const dataRef = useRef();
  dataRef.current = data;
  const queryRef = useRef();
  queryRef.current = dataQuery;

  // Functions for running the query/refreshing the data
  const refreshData = () => {
    let query = queryRef?.current;
    query?._page && query?._pageSize && dataQueryFunc(query);
  };
  const { initiateAutoRefresh, startRefreshInterval, resetAutoRefresh } =
    useAutoRefresh(refreshData, autoRefreshInterval);
  const runQuery = () => {
    initiateAutoRefresh();
  };

  const debouncedRunQuery = useMemo(() => debounce(runQuery, 800), []);

  const [isLoading, setIsLoading] = useState(false);
  const [pagingInfo, setPagingInfo] = useState({
    pageNumber: 1,
    pageSize: 50,
    totalPages: 1,
    sort: '',
  });
  const [hasUserClicked, setHasUserClicked] = useState(false);
  const [records, setRecords] = useState(initialData?.records);
  const [openFilters, setOpenFilters] = useState([]);

  useEffect(() => {
    document.addEventListener('mousedown', handleMouseClick);

    if (initialData) {
      startRefreshInterval();
      setIsLoading(false);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseClick);
    };
  }, []);

  useEffect(() => {
    if (!hasUserClicked && document.activeElement)
      document.activeElement.blur();
  }, [document.activeElement]);

  // Loading and refreshing data
  useEffect(() => {
    refreshKey && refreshData();
  }, [refreshKey]);

  useEffect(() => {
    if (dataQuery) {
      resetAutoRefresh();
      if (!data && !initialData) {
        setIsLoading(true);
        runQuery();
      } else {
        setIsLoading(true);
        debouncedRunQuery(dataQuery);
      }
    } else {
      setIsLoading(false);
    }
  }, [dataQuery]);

  useEffect(() => {
    if (filterBy && Object.entries(filterBy).length) {
      let newOpenFilters = new Set([...openFilters, ...Object.keys(filterBy)]);

      setOpenFilters((currentOpenFilters) => {
        return areEquivalent(currentOpenFilters, [...newOpenFilters])
          ? currentOpenFilters
          : [...newOpenFilters];
      });
    }
  }, [filterBy]);

  useEffect(() => {
    if (!data && !initialData) {
      resetAutoRefresh();
      return;
    }

    const currentData = data || initialData;
    setIsLoading(false);
    setRecords(currentData?.records || []);

    let totalNumberPages = currentData.count
      ? Math.ceil(currentData.count / pagingInfo.pageSize)
      : 1;
    if (totalNumberPages !== pagingInfo.totalPages) {
      setPagingInfo({
        ...pagingInfo,
        pageNumber: 1,
        totalPages: totalNumberPages,
      });
    }

    startRefreshInterval();
  }, [data]);

  useEffect(() => {
    if (persistentPaging) {
      setPagingInfo({
        ...pagingInfo,
        ...persistentPaging,
      });
    }
  }, [persistentPaging]);

  useEffect(() => {
    onUpdatePagingInfo(pagingInfo);
  }, [pagingInfo]);

  const handleMouseClick = () => {
    setHasUserClicked(true);
  };

  const clearFilterClicked = (filterValue) => {
    setOpenFilters((currentOpenFilters) =>
      [...currentOpenFilters].filter((header) => header !== filterValue),
    );
    onClearFilter(filterValue);
  };

  const headerRenderer = (ci) => {
    let header = headers[ci];

    if (!header) {
      return (
        <ColumnHeaderCell
          key={ci}
          selectCellsOnMenuClick={false}
          className={`${styles.data_table_cell_header} ${
            styles.hidden_header
          } ${smallFont ? styles.short_cell_header : ''}`}>
          <div className={styles.data_table_header_div} />
        </ColumnHeaderCell>
      );
    }

    const columnHeaderContent = header.renderHeader
      ? header.renderHeader(header)
      : header.label || '';

    const dummyDiv = <div />;
    let rightHeaderButton = dummyDiv;

    let isColumnBeingFiltered =
      header.filterable && openFilters.includes(header.value);

    let sortColumnHeaderContent;
    if (header.sortable) {
      let sortField = header.sortFieldName || header.fieldName || header.value;
      const sortAsc = () =>
        setPagingInfo({ ...pagingInfo, pageNumber: 1, sort: sortField });
      const sortDesc = () =>
        setPagingInfo({
          ...pagingInfo,
          pageNumber: 1,
          sort: `${sortField}$DESC`,
        });

      let isSortAsc = pagingInfo.sort === sortField;
      let isSortDesc = pagingInfo.sort === `${sortField}$DESC`;

      sortColumnHeaderContent = (
        <span
          className={styles.header_text}
          style={{ cursor: 'pointer' }}
          onClick={isSortAsc ? sortDesc : sortAsc}>
          {columnHeaderContent}
        </span>
      );
      rightHeaderButton = !isColumnBeingFiltered && (
        <Tooltip minimal content={`Sort${isSortAsc ? ' descending' : ''}`}>
          <Button
            small
            className={styles.header_sort_icon}
            icon={
              isSortAsc
                ? 'caret-down'
                : isSortDesc
                ? 'caret-up'
                : 'double-caret-vertical'
            }
            minimal
            onClick={isSortAsc ? sortDesc : sortAsc}
          />
        </Tooltip>
      );
    }

    let filterContent;
    if (header.filterable) {
      filterContent = !isColumnBeingFiltered ? (
        <Tooltip
          className={styles.header_text_filterable}
          minimal
          content={'Click to filter'}>
          <span
            onClick={() =>
              setOpenFilters((currentOpenFilters) => [
                ...currentOpenFilters.filter((f) => f !== header.value),
                header.value,
              ])
            }>
            {header.label}
            <Icon className={styles.header_filter_icon} icon='filter-list' />
          </span>
        </Tooltip>
      ) : (
        <>
          {columnHeaderContent}
          <Tooltip minimal content={'Clear filter'}>
            <Button
              small
              minimal
              icon='small-cross'
              className={styles.header_clear_filter_button}
              onClick={() => clearFilterClicked(header.value)}
            />
          </Tooltip>
        </>
      );
    }

    let headerDiv = (
      <div
        className={[
          styles.data_table_header_div,
          ...(header.moneyColumn ? [styles.money_cell] : []),
          ...(header.centeredHeader ? [styles.centered_header] : []),
          ...(smallFont ? [styles.small_font] : []),
        ].join(' ')}>
        {filterContent || sortColumnHeaderContent || columnHeaderContent}
        {rightHeaderButton}
        {columnHeaderContent === '' && header.sortable && dummyDiv}
      </div>
    );

    let headerClass =
      header.isIconCol || hiddenColumns.has(header.value)
        ? styles.hidden_header
        : styles.header_with_resizer;

    return (
      <ColumnHeaderCell
        key={ci}
        selectCellsOnMenuClick={false}
        style={{ boxShadow: 'none' }}
        className={`${headerClass} ${
          smallFont ? styles.short_cell_header : ''
        }`}>
        {headerDiv}
      </ColumnHeaderCell>
    );
  };
  const doubleClickCell = (record, e) => {
    navigateToDetailsFunc && navigateToDetailsFunc(record);
  };

  const getRenderedCell = (ri, ci) => {
    let header = headers[ci];
    let record = records ? records[ri] : undefined;
    let index = `${ci}-${ri}`;
    let isHidden = hiddenColumns.has(header.value);

    let isSelectedRow =
      selectedItems &&
      record &&
      record[idField] &&
      selectedItems.includes(record[idField]);

    let cellClass =
      !header || ri === numberOfRows - 1
        ? styles.table_cell
        : styles.table_cell_bordered;

    if (!header || !record) {
      return (
        <Cell
          key={index}
          loading={!header?.isIconCol && isLoading}
          className={`${cellClass} ${
            isSelectedRow ? styles.selected_cell : ''
          }`}
          truncated={false}
          tabIndex={-1}
          interactive={false}
        />
      );
    }
    const fieldName = header.fieldName || header.value;

    let cellData = record[fieldName];
    let cellContent = isHidden
      ? ''
      : header.render
      ? header.render(record)
      : header.format
      ? header.format(cellData)
      : header.moneyColumn
      ? `$${formatMoney(cellData)}`
      : cellData;

    let highlightCell = highlightFunc && highlightFunc(record);

    return (
      <Cell
        key={index}
        loading={!header.isIconCol && isLoading}
        className={[
          cellClass,
          ...(!isLoading ? [styles.align_center] : []),
          ...(header.isIconCol ? [styles.icon_cell] : []),
          ...(header.moneyColumn ? [styles.money_cell] : []),
          ...(highlightCell ? [styles.highlighted_cell] : []),
          ...(isSelectedRow ? [styles.selected_cell] : []),
          ...(smallFont ? [styles.small_font] : []),
          ...(header.isRequired && !cellContent
            ? [styles.table_cell_required]
            : []),
        ].join(' ')}
        interactive
        style={highlightCell && !header.isIconCol ? { opacity: 0.7 } : {}}
        wrapText={header.isIconCol || !!header.truncated || header.wrapText}
        truncated={!header.isIconCol && !header.truncated}>
        <div
          className={header.centeredHeader ? styles.text_center : ''}
          onDoubleClick={(e) => doubleClickCell(record, e)}>
          <ConditionalWrapper
            condition={!!header.truncated}
            wrapper={(children) => (
              <TruncatedFormat2
                className={styles.truncated_cell}
                detectTruncation>
                {children || ''}
              </TruncatedFormat2>
            )}>
            {cellContent}
          </ConditionalWrapper>
        </div>
      </Cell>
    );
  };

  const getCellClipboardData = (ri, ci) => {
    let header = headers[ci];
    let record = records ? records[ri] : undefined;

    if (!header || !record) {
      return '';
    }

    const fieldName = header.fieldName || header.value;
    let cellData = record[fieldName];
    return header.getText
      ? header.getText(record)
      : header.format
      ? header.format(cellData)
      : cellData;
  };

  const getEmptyCell = (ri) => {
    let record = records ? records[ri] : undefined;

    let isSelectedRow =
      selectedItems &&
      record &&
      record[idField] &&
      selectedItems.includes(record[idField]);

    return (
      <Cell
        key={ri}
        loading={false}
        isFocused={false}
        className={`${styles.table_cell} ${
          isSelectedRow ? styles.selected_cell : ''
        }`}
        style={{ boxShadow: 'none', padding: 0 }}
        truncated={false}
        interactive={false}
      />
    );
  };

  const rowHeaderRenderer = (ri) => {
    let record = records ? records[ri] : undefined;

    return includeCheckBoxCol && record ? (
      <RowHeaderCell
        key={ri}
        className={[
          styles.data_table_row_header_checkbox,
          ...(selectedItems.includes(record[idField])
            ? [styles.selected_cell]
            : []),
        ].join(' ')}>
        <div
          className={styles.data_table_clickable_cell}
          onDoubleClick={(e) => doubleClickCell(record, e)}>
          <div className={styles.checkbox_cell}>
            <ShowOnHoverWrapper
              key={record[idField]}
              alwaysShowCondition={selectedItems.length > 0}>
              <Checkbox
                key={record[idField]}
                className={styles.checkbox}
                checked={selectedItems.includes(record[idField])}
                onChange={(e) => {
                  onToggleCheckboxForItem(record);
                  e.stopPropagation();
                }}
              />
            </ShowOnHoverWrapper>
          </div>
        </div>
      </RowHeaderCell>
    ) : (
      <RowHeaderCell
        key={ri}
        className={styles.data_table_row_header}
        nameRenderer={(name, index) => {
          return (
            <div
              className={styles.data_table_clickable_cell}
              onDoubleClick={(e) => doubleClickCell(record, e)}
            />
          );
        }}
      />
    );
  };

  const exportData = () => {
    if (!exportDataFunc || !dataQuery) return;
    exportDataFunc(dataQuery);
  };

  let exportButton = exportDataFunc && (
    <ExportButton exportDataFunc={exportData} disabled={!records?.length} />
  );

  let showToolBox =
    !!title ||
    !!exportDataFunc ||
    (Array.isArray(actionButtons)
      ? actionButtons?.length > 0
      : !!actionButtons);

  let tableBoxStyle = showToolBox
    ? openFilters?.length
      ? styles.table_box_yes_tools_yes_filter
      : styles.table_box_yes_tools_no_filter
    : openFilters?.length
    ? styles.table_box_no_tools_yes_filter
    : styles.table_box_no_tools_no_filter;

  let numberOfRows = isLoading
    ? 10
    : Math.min(records?.length || 0, pagingInfo.pageSize);

  const showHideColumn = (header) => {
    let toggleValue = header.value;

    setHiddenColumns((prev) => {
      const newSet = new Set(prev);
      if (prev.has(toggleValue)) {
        newSet.delete(toggleValue);
        return newSet;
      } else {
        return newSet.add(toggleValue);
      }
    });
  };

  let tableColumns =
    columnWidthsForTable?.length > 0 &&
    columnWidthsForTable.map((_col, index) => {
      if (index === headers.length) {
        return (
          <Column
            key={'END'}
            id={'END'}
            name={''}
            columnHeaderCellRenderer={headerRenderer}
            cellRenderer={getEmptyCell}
          />
        );
      }

      let header = headers[index];
      return (
        <Column
          key={index}
          id={index}
          name={header?.label}
          columnHeaderCellRenderer={headerRenderer}
          cellRenderer={getRenderedCell}
        />
      );
    });

  const fontSizeMenu = (
    <Menu>
      <h6 className='bp5-heading'>Text Size</h6>
      <MenuItem
        text={'Small'}
        icon={smallFont ? 'tick' : 'blank'}
        onClick={() => {
          setSmallFont((val) => !val);
        }}
      />
      <MenuItem
        text={'Regular'}
        icon={!smallFont ? 'tick' : 'blank'}
        onClick={() => {
          setSmallFont((val) => !val);
        }}
      />
    </Menu>
  );

  const columnSettingsMenu = (
    <Menu>
      <h6 className='bp5-heading'>Show/Hide Columns</h6>
      {headers
        .filter((header) => !!header.value && !!header.label)
        .map((header, index) => {
          let isHidden = hiddenColumns.has(header.value);
          return (
            <MenuItem
              key={index}
              text={header.label}
              icon={isHidden ? 'blank' : 'tick'}
              disabled={
                !isHidden &&
                hiddenColumns.size ===
                  headers.filter((header) => !!header.value && !!header.label)
                    .length -
                    1
              }
              onClick={() => {
                showHideColumn(header);
              }}
            />
          );
        })}
    </Menu>
  );

  const tableSettingsMenu = (
    <div className={styles.table_settings_box}>
      {fontSizeMenu}
      {columnSettingsMenu}
    </div>
  );

  const showHideSettingsButton = (
    <Popover minimal placement={'left-start'} content={tableSettingsMenu}>
      <Tooltip content={'Table Settings'} interactionKind={'hover-target'}>
        <Button
          minimal
          className={styles.table_settings_button}
          icon={'settings'}
        />
      </Tooltip>
    </Popover>
  );
  const measureAndAdjustTable = () => {
    adjustRowHeightToFit && blueprintTable.current.resizeRowsByTallestCell();
    measureContainer();
  };

  let defaultColWidth = document.getElementById('app_content_container')
    ? document.getElementById('app_content_container').clientWidth /
      headers.length
    : 800;

  return (
    <div className={styles.outer_container}>
      <div ref={tableBox} className={tableBoxStyle}>
        {showToolBox && (
          <div className={styles.table_tools_box}>
            {title && <H3 style={{ marginBottom: 0 }}>{title}</H3>}
            <div style={{ marginLeft: 'auto' }}>
              {actionButtons}
              {exportButton}
              {showHideSettingsButton}
            </div>
          </div>
        )}
        {headers?.length > 0 && (
          <>
            <div
              className={
                isBusy
                  ? styles.data_table_container_busy
                  : styles.data_table_container
              }>
              {/*Checkbox header*/}
              {includeCheckBoxCol && selectedItems.length > 0 && (
                <div className={styles.checkbox_header_containing}>
                  <div className={styles.checkbox_header}>
                    <Checkbox
                      className={styles.checkbox}
                      checked={selectedItems.length > 0}
                      indeterminate={selectedItems.length > 0}
                      onClick={onToggleParentCheckbox}
                    />
                  </div>
                </div>
              )}

              {/*Blueprint.js Table component*/}
              <Table2
                ref={blueprintTable}
                className={`${styles.data_table} ${
                  smallFont ? 'small_font' : ''
                }`}
                cellRendererDependencies={[
                  isLoading,
                  openFilters?.length,
                  records,
                  ...rerenderDependencies,
                ]}
                loadingOptions={[TableLoadingOption.CELLS]}
                selectionModes={[RegionCardinality.CELLS]}
                columnWidths={columnWidthsForTable}
                defaultColumnWidth={defaultColWidth}
                minColumnWidth={60}
                onColumnWidthChanged={updateColumnWidth}
                enableRowHeader
                rowHeaderCellRenderer={rowHeaderRenderer}
                numRows={numberOfRows}
                defaultRowHeight={rowHeight}
                minRowHeight={rowHeight}
                onCompleteRender={measureAndAdjustTable}
                getCellClipboardData={getCellClipboardData}>
                {tableColumns}
              </Table2>
            </div>

            {/*No records from query*/}
            {!isLoading && records && records.length === 0 && (
              <NoContentBox
                label={'No Records'}
                style={{ backgroundColor: 'white' }}
              />
            )}

            {/*Paging Footer*/}
            <TablePagingContext.Provider
              value={{
                pagingInfo,
                setPagingInfo,
                rowCount: data?.count || 0,
              }}>
              <PagingFooter />
            </TablePagingContext.Provider>
          </>
        )}
      </div>
    </div>
  );
};

export const useTablePagingContext = () => useContext(TablePagingContext);

DataTableComponent.propTypes = {
  data: PropTypes.shape({ records: PropTypes.array, count: PropTypes.number }),
  initialData: PropTypes.shape({
    records: PropTypes.array,
    count: PropTypes.number,
  }),
  dataQuery: PropTypes.object,
  dataQueryFunc: PropTypes.func,
  autoRefreshInterval: PropTypes.number,
  refreshKey: PropTypes.any,
  isBusy: PropTypes.bool,
  headers: PropTypes.array.isRequired,
  hiddenColumns: PropTypes.object,
  setHiddenColumns: PropTypes.func,
  onUpdatePagingInfo: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  persistentPaging: PropTypes.object,
  rowHeight: PropTypes.number,
  onClearFilter: PropTypes.func,
  filterBy: PropTypes.object,
  exportDataFunc: PropTypes.func,
  actionButtons: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
  rerenderDependencies: PropTypes.array,
  measureContainer: PropTypes.func,
  updateColumnWidth: PropTypes.func,
  columnWidthsForTable: PropTypes.array,
  adjustRowHeightToFit: PropTypes.bool,
  smallFont: PropTypes.bool,
  setSmallFont: PropTypes.func,
  includeCheckBoxCol: PropTypes.bool,
  idField: PropTypes.string,
  selectedItems: PropTypes.array,
  onToggleCheckboxForItem: PropTypes.func,
  onToggleParentCheckbox: PropTypes.func,
  highlightFunc: PropTypes.func,
  navigateToDetailsFunc: PropTypes.func,
};

export default memo(DataTableComponent);
